import React, { useEffect, useMemo } from 'react';

import { RouterUtils, useURLSearchParams, useNavigate } from 'application/providers/RouterProvider';
import { useAuthContext, LocalAccessRoles } from 'application/providers/AuthProvider';
import AnalyticsService, {
  EventNameAMP,
  EventNameGTM,
  LoginPathValues,
  ParamNameAMP,
  ParamNameGTM,
} from 'application/services/AnalyticsService';
import usePageShownAmpSource from 'application/hooks/usePageShownAmpSource';
import { AuthQueryKeys, IApplicationPage } from 'application/pages/types';
import { REGISTER_PAGE_PATH } from 'application/pages/RegisterPage';
import { Platform } from 'application/pages/constants';
import CleanMain from 'application/layout/CleanLayout/CleanMain';
import AuthForm from 'application/components/AuthForm';
import { AuthFormMode, AuthFormStep } from 'application/components/AuthForm/types';
import BrowserUtils from 'application/utils/BrowserUtils';
import PhoneUtils from 'application/utils/PhoneUtils';
import ActivationService from 'application/services/ActivationService';
import { LOGIN_PAGE_PATH_ALIAS } from 'application/consts';

import { AuthTokenType, LoginQueryKeys, RegisterQueryKeys, UserDataType } from 'domain/auth/types';

import styles from './index.module.scss';

import { TEXTS } from './texts';

const SEARCH_PARAMS = [
  AuthQueryKeys.FROM,
  AuthQueryKeys.STEP,
  LoginQueryKeys.EMAIL,
  LoginQueryKeys.PHONE,
  AuthQueryKeys.PLATFORM,
  RegisterQueryKeys.PROMOCODE,
];

const LoginPage: IApplicationPage = () => {
  const authContext = useAuthContext();

  const isActivationFlow = ActivationService.isActivationFlow();

  const ampSource = usePageShownAmpSource();
  const inIframe = BrowserUtils.inIframe();

  const navigate = useNavigate();

  const [from, queryStep, email, phone, platform, promocode] = useURLSearchParams(SEARCH_PARAMS);

  const step = (queryStep as AuthFormStep) || AuthFormStep.LEGACY_AUTH;

  const [currentPhone, setCurrentPhone] = React.useState<string | undefined>(undefined);

  const isSBBIDAvailable = !inIframe && !email && !isActivationFlow && platform !== Platform.IOS;

  const initData = useMemo(
    () => ({
      email: email || undefined,
      phone: phone ? PhoneUtils.parsePhoneNumberFromString(decodeURIComponent(phone))?.number : undefined,
    }),
    [email, phone]
  );

  const getQueryParams = () => {
    const result: Record<string, string> = {};

    if (!from && !promocode) return undefined;

    if (from) {
      result.from = from;
    }

    if (promocode) {
      result.promocode = promocode;
    }

    return result;
  };

  const registerLinkTo = RouterUtils.generatePath(REGISTER_PAGE_PATH, undefined, getQueryParams());

  const onSuccess = (authToken: AuthTokenType, userData: UserDataType) => {
    authContext.setAuthData({
      authToken,
      userData,
    });

    AnalyticsService.event(EventNameAMP.USER_LOGGED_IN, {
      [ParamNameAMP.LOG_IN_PATH]: LoginPathValues.DEFAULT,
    });

    AnalyticsService.eventGTM(EventNameGTM.AUTH_SUCCESS, {
      [ParamNameGTM.ACCOUNT_CREATED_DATE]: userData.mainCompany?.created,
    });
  };

  const onPhoneChange = (phone: string) => {
    setCurrentPhone(phone);
  };

  const onStepChange = (step: AuthFormStep, phone?: string) => {
    navigate(
      RouterUtils.generatePath(LOGIN_PAGE_PATH_ALIAS, undefined, {
        ...RouterUtils.getURLSearchParamsAsObject(),
        step,
        phone: phone ? encodeURIComponent(phone) : undefined,
      })
    );
  };

  useEffect(() => {
    AnalyticsService.event(EventNameAMP.LOGIN_PAGE_SHOWN, {
      [ParamNameAMP.SOURCE]: ampSource,
    });
  }, []);

  return (
    <CleanMain
      title={TEXTS.DOC_TITLE}
      contentContainerAs="div">
      {(step === AuthFormStep.PHONE_INPUT || step === AuthFormStep.LEGACY_AUTH) && (
        <h3 className={styles.LoginPageTitle}>{TEXTS.PAGE_TITLE}</h3>
      )}

      {step === AuthFormStep.CODE_INPUT && currentPhone && (
        <span className={styles.LoginPageDescription}>
          {TEXTS.SUBTITLE_CODE_DESC(PhoneUtils.formatPhoneNumber(currentPhone))}
        </span>
      )}

      {(step === AuthFormStep.PHONE_INPUT || step === AuthFormStep.LEGACY_AUTH) && !isActivationFlow && (
        <div className={styles.LoginPageRegister}>{TEXTS.BUTTON_REGISTER_TITLE(registerLinkTo)}</div>
      )}

      <AuthForm
        className={styles.LoginPageForm}
        isSBBIDAvailable={isSBBIDAvailable}
        step={step}
        initData={initData}
        mode={AuthFormMode.LOGIN}
        onPhoneChange={onPhoneChange}
        onStepChange={onStepChange}
        onSuccess={onSuccess}
      />
    </CleanMain>
  );
};

LoginPage.config = {
  routes: [{ path: LOGIN_PAGE_PATH_ALIAS }],
  access: [LocalAccessRoles.ANONYMOUS],
};

export default LoginPage;
